import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Pencil, Check, X, MapPin } from 'lucide-react';
import { api } from '@/utils/api';
import { canManageMarkets, getStoredUser } from '@/utils/roleGate';

// S2-assets (Brian 2026-09-29 model): Markets = the company's
// offices/locations. This screen closes the "demo-seed only, no create
// UI/API" gap — create/rename/delete for management+admin roles (server
// enforces the same roles; this UI hides what the server 403s).

interface MarketRow {
  id: number;
  name: string;
  region: string | null;
}

export function LocationsPage() {
  const storedUser = getStoredUser();
  const canManage = canManageMarkets(storedUser.role);

  const [markets, setMarkets] = useState<MarketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);

  const [newName, setNewName] = useState('');
  const [newRegion, setNewRegion] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editRegion, setEditRegion] = useState('');

  const loadMarkets = async () => {
    try {
      setLoading(true);
      const res = await api.get('/markets');
      if (res.data.success) {
        setMarkets(res.data.data ?? []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load locations — check your connection and try again');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMarkets();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.post('/markets', {
        name: newName.trim(),
        region: newRegion.trim() || null,
      });
      if (res.data.success) {
        setNotice(`“${res.data.data.name}” added — it now appears in asset Office/Market pickers.`);
        setNewName('');
        setNewRegion('');
        await loadMarkets();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not create the location — check the name and try again');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (m: MarketRow) => {
    setEditingId(m.id);
    setEditName(m.name);
    setEditRegion(m.region || '');
  };

  const handleSaveEdit = async () => {
    if (editingId == null) return;
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.patch(`/markets/${editingId}`, {
        name: editName.trim(),
        region: editRegion.trim() || null,
      });
      if (res.data.success) {
        setNotice(`Saved — “${res.data.data.name}” updated.`);
        setEditingId(null);
        await loadMarkets();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save the change — try again');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m: MarketRow) => {
    setError('');
    setNotice('');
    setSaving(true);
    try {
      const res = await api.delete(`/markets/${m.id}`);
      if (res.data.success) {
        setNotice(res.data.message);
        await loadMarkets();
      }
    } catch (err: any) {
      // 409 (still referenced) explains what to move where — show it verbatim.
      setError(err.response?.data?.message || 'Could not delete the location — try again');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/assets" className="text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">Locations</h1>
              <p className="text-sm text-gray-600">
                Offices and markets your assets, crews, and plans belong to
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
        )}
        {notice && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">{notice}</div>
        )}

        {!canManage ? (
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-700">
              Locations are managed by admins and managers. Ask your project manager, safety manager,
              or an admin to add or rename an office — or contact your Conformara admin.
            </p>
          </div>
        ) : (
          <>
            {/* Create */}
            <form onSubmit={handleCreate} className="mb-6 bg-white rounded-lg shadow p-6">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Add a Location</h2>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Indy Office"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Region</label>
                  <input
                    type="text"
                    value={newRegion}
                    onChange={(e) => setNewRegion(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Midwest (optional)"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={saving || !newName.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Plus className="h-4 w-4" /> Add Location
                  </button>
                </div>
              </div>
            </form>

            {/* List */}
            <div className="bg-white rounded-lg shadow">
              {loading ? (
                <p className="p-6 text-sm text-gray-500">Loading locations…</p>
              ) : markets.length === 0 ? (
                <div className="p-6 text-center">
                  <MapPin className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-700 font-medium">No locations yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Add your first office above — assets and crews can then be assigned to it.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {markets.map((m) => (
                    <li key={m.id} className="p-4 flex items-center gap-3">
                      {editingId === m.id ? (
                        <>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded"
                          />
                          <input
                            type="text"
                            value={editRegion}
                            onChange={(e) => setEditRegion(e.target.value)}
                            className="w-40 px-2 py-1 border border-gray-300 rounded"
                            placeholder="Region"
                          />
                          <button
                            onClick={handleSaveEdit}
                            disabled={saving || !editName.trim()}
                            className="p-2 text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                            title="Save"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <MapPin className="h-5 w-5 text-gray-400" />
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">{m.name}</p>
                            <p className="text-xs text-gray-500">{m.region || 'No region set'}</p>
                          </div>
                          <button
                            onClick={() => startEdit(m)}
                            className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                            title="Rename / set region"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m)}
                            disabled={saving}
                            className="p-2 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              A location that still has assets or crews cannot be deleted until they are moved —
              the delete will tell you exactly what is still attached.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
